import { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, FileText, Loader2, Check, X, Crop } from "lucide-react";
import { PhotoStraightenDialog } from "@/components/PhotoStraightenDialog";

// Paper order: the reseller attaches photos / scans of the paper order form signed by the client.
export const PaperOrderPanel = ({ onConfirm, busy }) => {
  const { t } = useI18n();
  const ref = useRef(null);
  const pdfRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [queue, setQueue] = useState([]);
  const isPdf = (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name);
  // Uploads one file; `orig` keeps the untouched photo so it can be re-edited, `at` replaces an existing entry.
  const upload = async (f, orig, at) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload?private=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const item = { path: data.path, name: f.name, pdf: isPdf(f), orig: isPdf(f) ? null : orig || f, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : "" };
      setFiles((xs) => (at == null ? [...xs, item] : xs.map((x, i) => (i === at ? item : x))));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("Envoi de la photo impossible"));
    } finally { setUploading(false); }
  };
  const add = async (list) => {
    const room = 10 - files.length - queue.length;
    if (list.length > room) toast.error(t("10 fichiers maximum par commande"));
    const keep = list.slice(0, Math.max(0, room));
    setQueue((q) => [...q, ...keep.filter((f) => !isPdf(f)).map((file) => ({ file }))]);
    for (const f of keep.filter(isPdf)) await upload(f);
  };
  const current = queue[0];
  const next = () => setQueue((q) => q.slice(1));
  return (
    <div className="flex flex-col gap-4" data-testid="paper-order-panel">
      <p className="text-sm text-muted-foreground">{t("Prenez en photo chaque page du bon de commande papier signé par le client, ou ajoutez la commande signée en PDF.")}</p>
      <div className="flex flex-wrap gap-3" data-testid="paper-files">
        {files.map((f, i) => (
          <div key={f.path} className="relative w-28 h-36 rounded-lg border border-border bg-muted overflow-hidden flex items-center justify-center" data-testid={`paper-file-${i}`}>
            {f.preview ? <img src={f.preview} alt={f.name} className="w-full h-full object-cover" /> : <div className="text-xs text-center p-2"><FileText className="w-6 h-6 mx-auto mb-1" />{f.name}</div>}
            <button type="button" onClick={() => setFiles((xs) => xs.filter((x) => x.path !== f.path))} className="absolute top-1 right-1 bg-background/90 rounded-full p-1" data-testid={`paper-file-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
            {f.orig && <button type="button" onClick={() => setQueue((q) => [...q, { file: f.orig, at: i }])} title={t("Recadrer et redresser la photo")}
              className="absolute bottom-1 right-1 bg-background/90 rounded-full p-1.5" data-testid={`paper-file-edit-${i}`}><Crop className="w-3.5 h-3.5" /></button>}
          </div>
        ))}
        <button type="button" onClick={() => ref.current?.click()} disabled={uploading || files.length >= 10}
          className="w-28 h-36 rounded-lg border-2 border-dashed border-primary/50 text-primary flex flex-col items-center justify-center gap-1 text-xs font-medium hover:bg-primary/5 transition-colors" data-testid="paper-add">
          {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <Camera className="w-6 h-6" />} {t("Ajouter une photo")}
        </button>
        <button type="button" onClick={() => pdfRef.current?.click()} disabled={uploading || files.length >= 10}
          className="w-28 h-36 rounded-lg border-2 border-dashed border-primary/50 text-primary flex flex-col items-center justify-center gap-1 text-xs font-medium hover:bg-primary/5 transition-colors" data-testid="paper-add-pdf">
          {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <FileText className="w-6 h-6" />} {t("Ajouter un PDF")}
        </button>
        <input ref={pdfRef} type="file" accept="application/pdf,.pdf" multiple className="hidden" data-testid="paper-input-pdf"
          onChange={(e) => { const l = [...(e.target.files || [])]; e.target.value = ""; if (l.length) add(l); }} />
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" multiple className="hidden" data-testid="paper-input"
          onChange={(e) => { const l = [...(e.target.files || [])]; e.target.value = ""; if (l.length) add(l); }} />
      </div>
      <div className="text-xs text-muted-foreground" data-testid="paper-hint">{t("La commande et les photos seront envoyées à vous et à contact@fautras.com ; Corinne Golfier la recevra une fois l'acompte reçu et le financement choisi. Le client ne reçoit pas d'e-mail.")}</div>
      <Button size="lg" onClick={() => onConfirm(files.map((f) => f.path))} disabled={busy || uploading || !!queue.length || !files.length} className="gap-2" data-testid="paper-confirm">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {t("Enregistrer la commande papier")}
      </Button>
      <PhotoStraightenDialog file={current?.file} onCancel={next}
        onSkip={(f) => { const c = current; next(); upload(f, f, c?.at); }}
        onDone={(f) => { const c = current; next(); upload(f, c.file, c.at); }} />
    </div>
  );
};
